import { useFieldVisibility as useFieldVisibilityContext } from '../contexts/FieldVisibilityContext';
import { FieldDefinition } from '../types';

/**
 * Hook for managing field visibility in forms and displays
 */
export const useFieldVisibility = () => {
  const context = useFieldVisibilityContext();

  /**
   * Filter a list of field keys to only include visible ones
   */
  const filterVisibleFields = (fieldKeys: string[]): string[] => {
    return fieldKeys.filter(key => context.isFieldVisible(key));
  };

  /**
   * Get visible fields from a list of field definitions
   */
  const getVisibleFields = (fields: FieldDefinition[]): FieldDefinition[] => {
    return fields.filter(field => context.isFieldVisible(field.key));
  };

  /**
   * Check if any fields in a group are visible
   */
  const hasVisibleFields = (fieldKeys: string[]): boolean => {
    return fieldKeys.some(key => context.isFieldVisible(key));
  };

  /**
   * Get field visibility status for multiple fields at once
   */
  const getFieldVisibilityMap = (fieldKeys: string[]): Record<string, boolean> => {
    return fieldKeys.reduce((map, key) => {
      map[key] = context.isFieldVisible(key);
      return map;
    }, {} as Record<string, boolean>);
  };

  return {
    ...context,
    filterVisibleFields,
    getVisibleFields,
    hasVisibleFields,
    getFieldVisibilityMap,
  };
};

export default useFieldVisibility;